import { useState } from "react";
import { IonButton, IonIcon } from "@ionic/react";
import { saveOutline } from "ionicons/icons";
import { useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "../../hooks/usePymbleApi";
import { displayMsg } from "pymble-ui/components";
import GroupPermissionsGrid, {
  type PhotoPostersMode,
} from "../GroupPermissionsGrid";
import type { GroupDetail } from "../../hooks/useGroupDetail";

import "./PermissionsTab.css";

interface PermissionsTabProps {
  groupId: string;
  group: GroupDetail;
}

const PermissionsTab: React.FC<PermissionsTabProps> = ({ groupId, group }) => {
  const { callApi } = usePymbleApi();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(group.Permission?.Policy ?? "");
  const [photoPosters, setPhotoPosters] = useState<PhotoPostersMode>(
    group.Settings?.PhotoPosters ?? "editors"
  );

  const handlePolicyChange = (newPolicy: Set<string>) => {
    setPolicy(Array.from(newPolicy).join("\n"));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await callApi("PUT", `/group/admin/${groupId}`, {
        data: {
          _id: groupId,
          Permission: { Policy: policy },
          PhotoPosters: photoPosters,
        },
      });

      queryClient.invalidateQueries({ queryKey: ["group-detail", groupId] });
      displayMsg("Permissions saved", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch {
      displayMsg("Failed to save permissions", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="permissions-tab">
      <GroupPermissionsGrid
        policy={policy}
        onPolicyChange={handlePolicyChange}
        photoPosters={photoPosters}
        onPhotoPostersChange={setPhotoPosters}
      />

      <div className="permissions-save">
        <IonButton
          onClick={handleSave}
          disabled={saving}
          color="primary"
          className="ion-text-capitalize"
        >
          <IonIcon slot="start" icon={saveOutline} />
          {saving ? "Saving..." : "Save Permissions"}
        </IonButton>
      </div>
    </div>
  );
};

export default PermissionsTab;
